import axios from 'axios';

const API_BASE_URL = '/api';

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  config => {
    const token = localStorage.getItem('access_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  error => {
    return Promise.reject(error);
  }
);

// Response interceptor to handle token refresh
api.interceptors.response.use(
  response => response,
  async error => {
    const original = error.config;

    if (error.response?.status === 401 && !original._retry) {
      original._retry = true;

      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        try {
          // Try to refresh the token
          const response = await axios.post('/api/auth/token/refresh/', {
            refresh: refreshToken,
          });

          const { access } = response.data;
          localStorage.setItem('access_token', access);

          // Retry the original request
          original.headers.Authorization = `Bearer ${access}`;
          return axios(original);
        } catch (refreshError) {
          // Refresh failed, redirect to login
          localStorage.removeItem('access_token');
          localStorage.removeItem('refresh_token');
          window.location.href = '/';
        }
      } else {
        // No refresh token, redirect to login
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        window.location.href = '/';
      }
    }

    return Promise.reject(error);
  }
);

// Auth API calls
export const register = async userData => {
  const response = await api.post('/auth/register/', userData);
  const { user, tokens } = response.data;

  // Store tokens
  localStorage.setItem('access_token', tokens.access);
  localStorage.setItem('refresh_token', tokens.refresh);

  return user;
};

export const login = async credentials => {
  const response = await api.post('/auth/login/', credentials);
  const { user, tokens } = response.data;

  // Store tokens
  localStorage.setItem('access_token', tokens.access);
  localStorage.setItem('refresh_token', tokens.refresh);

  return user;
};

export const logout = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
};

export const getCurrentUser = async () => {
  const response = await api.get('/auth/profile/');
  return response.data;
};

export const updateProfile = async profileData => {
  const response = await api.put('/auth/profile/', profileData);
  return response.data;
};

// Organizations API calls
export const getOrganizations = async () => {
  const response = await api.get('/organizations/');
  return response.data;
};

export const addMember = async (organizationId, memberData) => {
  const response = await api.post(
    `/organizations/${organizationId}/add_member/`,
    memberData
  );
  return response.data;
};

export const removeMember = async (organizationId, userId) => {
  await api.delete(`/organizations/${organizationId}/remove_member/`, {
    data: { user_id: userId },
  });
};

export const joinOrganizationWithKey = async signupKey => {
  const response = await api.post('/organizations/join_with_key/', {
    signup_key: signupKey,
  });
  return response.data;
};

// Maps API calls
export const getMaps = async () => {
  const response = await api.get('/maps/');
  return response.data;
};

export const createMap = async mapData => {
  const formData = new FormData();

  Object.keys(mapData).forEach(key => {
    if (mapData[key] !== null && mapData[key] !== undefined) {
      formData.append(key, mapData[key]);
    }
  });

  const response = await api.post('/maps/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const updateMap = async (id, mapData) => {
  const formData = new FormData();

  Object.keys(mapData).forEach(key => {
    if (mapData[key] !== null && mapData[key] !== undefined) {
      formData.append(key, mapData[key]);
    }
  });

  const response = await api.put(`/maps/${id}/`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const deleteMap = async id => {
  await api.delete(`/maps/${id}/`);
};

// Map Data API calls
export const getMapData = async mapId => {
  const response = await api.get(`/mapdata/?map=${mapId}`);
  return response.data;
};

export const createMapData = async mapDataItem => {
  const response = await api.post('/mapdata/', mapDataItem);
  return response.data;
};

export const updateMapData = async (id, mapDataItem) => {
  const response = await api.put(`/mapdata/${id}/`, mapDataItem);
  return response.data;
};

export const deleteMapData = async id => {
  await api.delete(`/mapdata/${id}/`);
};

// Custom Pin Icons API calls
export const getCustomPinIcons = async () => {
  const response = await api.get('/custom-pin-icons/');
  return response.data;
};

export const createCustomPinIcon = async pinIconData => {
  const formData = new FormData();

  Object.keys(pinIconData).forEach(key => {
    if (pinIconData[key] !== null && pinIconData[key] !== undefined) {
      formData.append(key, pinIconData[key]);
    }
  });

  const response = await api.post('/custom-pin-icons/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const updateCustomPinIcon = async (id, pinIconData) => {
  // Check if we have a file to upload
  const hasFile = pinIconData.icon_image instanceof File;

  let requestData;
  let headers = {};

  if (hasFile) {
    // Use FormData for file uploads with PATCH
    const formData = new FormData();
    Object.keys(pinIconData).forEach(key => {
      if (pinIconData[key] !== null && pinIconData[key] !== undefined) {
        formData.append(key, pinIconData[key]);
      }
    });
    requestData = formData;
    headers['Content-Type'] = 'multipart/form-data';
  } else {
    // Use JSON for text-only updates with PATCH
    requestData = pinIconData;
  }

  // Use PATCH for partial updates instead of PUT
  const response = await api.patch(`/custom-pin-icons/${id}/`, requestData, {
    headers,
  });
  return response.data;
};

export const deleteCustomPinIcon = async id => {
  await api.delete(`/custom-pin-icons/${id}/`);
};

// Vendor API endpoints
export const getVendors = async () => {
  const response = await api.get('/vendors/');
  return response.data;
};

export const createVendor = async vendorData => {
  const response = await api.post('/vendors/', vendorData);
  return response.data;
};

// Map Background Image API endpoints
export const getMapBackgroundImages = async () => {
  const response = await api.get('/background-images/');
  return response.data;
};

export const createMapBackgroundImage = async backgroundImageData => {
  const formData = new FormData();

  Object.keys(backgroundImageData).forEach(key => {
    if (
      backgroundImageData[key] !== null &&
      backgroundImageData[key] !== undefined
    ) {
      formData.append(key, backgroundImageData[key]);
    }
  });

  const response = await api.post('/background-images/', formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const updateMapBackgroundImage = async (id, backgroundImageData) => {
  const formData = new FormData();

  Object.keys(backgroundImageData).forEach(key => {
    if (
      backgroundImageData[key] !== null &&
      backgroundImageData[key] !== undefined
    ) {
      formData.append(key, backgroundImageData[key]);
    }
  });

  const response = await api.put(`/background-images/${id}/`, formData, {
    headers: {
      'Content-Type': 'multipart/form-data',
    },
  });
  return response.data;
};

export const deleteMapBackgroundImage = async id => {
  await api.delete(`/background-images/${id}/`);
};

// Media API endpoints
export const getMedia = async () => {
  const response = await api.get('/media/');
  return response.data;
};

// Feature Flags API endpoints
export const getFeatureFlags = async () => {
  const response = await api.get('/features/');
  return response.data;
};

// Entity Models API
export const getEntityModels = async () => {
  const response = await api.get('/entity-models/');
  return response.data;
};

export const createEntityModel = async entityModelData => {
  const response = await api.post('/entity-models/', entityModelData);
  return response.data;
};

export const updateEntityModel = async (id, entityModelData) => {
  const response = await api.put(`/entity-models/${id}/`, entityModelData);
  return response.data;
};

export const deleteEntityModel = async id => {
  await api.delete(`/entity-models/${id}/`);
};

// Entities API
export const getEntities = async (params = {}) => {
  const response = await api.get('/entities/', { params });
  return response.data;
};

export const createEntity = async entityData => {
  const response = await api.post('/entities/', entityData);
  return response.data;
};

export const updateEntity = async (id, entityData) => {
  const response = await api.put(`/entities/${id}/`, entityData);
  return response.data;
};

export const deleteEntity = async id => {
  await api.delete(`/entities/${id}/`);
};

export default api;

// Follow pagination so group selectors never silently omit devices.
export const getAllPages = async (url, params = {}) => {
  const items = [];
  let next = url;
  while (next) {
    const response = await api.get(next, { params });
    const data = response.data;
    items.push(...(Array.isArray(data) ? data : data.results || []));
    next = Array.isArray(data) ? null : data.next;
    params = {};
  }
  return items;
};
export const getMonitorGroups = organization =>
  getAllPages('/monitor-groups/', { organization });
export const saveMonitorGroup = (id, data) =>
  id
    ? api.patch(`/monitor-groups/${id}/`, data)
    : api.post('/monitor-groups/', data);
export const deleteMonitorGroup = id => api.delete(`/monitor-groups/${id}/`);
export const getMonitorGroupFields = organization =>
  getAllPages('/monitor-group-fields/', { organization });
export const saveMonitorGroupField = (id, data) =>
  id
    ? api.patch(`/monitor-group-fields/${id}/`, data)
    : api.post('/monitor-group-fields/', data);
export const deleteMonitorGroupField = id =>
  api.delete(`/monitor-group-fields/${id}/`);
